import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { 
    TrendingUp, 
    DollarSign, 
    CreditCard, 
    Package, 
    Calendar as CalendarIcon, 
    ArrowUpRight, 
    ArrowDownRight 
} from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { toast } from 'sonner';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Legend
} from 'recharts';

// ENV Config
const ENV_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
let BACKEND_URL = '';
if (!ENV_BACKEND_URL || ENV_BACKEND_URL === '') {
  BACKEND_URL = window.location.origin; 
} else {
  BACKEND_URL = ENV_BACKEND_URL;
}
if (BACKEND_URL.endsWith('/api')) {
  BACKEND_URL = BACKEND_URL.slice(0, -4);
}
const API = `${BACKEND_URL}/api`;

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

export default function SuperAdminDashboard() {
    const [date, setDate] = useState({
        from: new Date(new Date().setDate(new Date().getDate() - 30)), // Last 30 days default
        to: new Date()
    });
    
    const [summary, setSummary] = useState(null);
    const [trends, setTrends] = useState([]);
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [topProducts, setTopProducts] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (date?.from && date?.to) {
            fetchData();
        }
    }, [date]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const params = {
                start_date: date.from.toISOString(),
                end_date: date.to.toISOString()
            };

            const [summaryRes, trendsRes, paymentsRes, productsRes] = await Promise.all([
                axios.get(`${API}/super-dashboard/summary`, { params }),
                axios.get(`${API}/super-dashboard/trends`, { params }),
                axios.get(`${API}/super-dashboard/payment-methods`, { params }),
                axios.get(`${API}/super-dashboard/top-products`, { params })
            ]);

            setSummary(summaryRes.data);
            setTrends(trendsRes.data);
            setPaymentMethods(paymentsRes.data);
            setTopProducts(productsRes.data);
        } catch (error) {
            console.error("Error fetching dashboard data", error);
            toast.error("Error al cargar datos del panel gerencial");
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (val) => {
        return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(val);
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight">Panel Gerencial</h1>
                    <p className="text-muted-foreground">
                        Visión integral del rendimiento del negocio
                    </p>
                </div>
                
                <Popover>
                    <PopoverTrigger asChild>
                        <Button variant="outline" className="w-[280px] justify-start text-left font-normal">
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {date?.from ? (
                                date.to ? (
                                    <>
                                        {format(date.from, "LLL dd, y", { locale: es })} -{" "}
                                        {format(date.to, "LLL dd, y", { locale: es })}
                                    </>
                                ) : (
                                    format(date.from, "LLL dd, y", { locale: es })
                                )
                            ) : (
                                <span>Seleccionar fechas</span>
                            )}
                        </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="end">
                        <Calendar
                            initialFocus
                            mode="range"
                            defaultMonth={date?.from}
                            selected={date}
                            onSelect={setDate}
                            numberOfMonths={2}
                            locale={es}
                        />
                    </PopoverContent>
                </Popover>
            </div>

            {loading ? (
                <div className="h-96 flex items-center justify-center">Cargando datos...</div>
            ) : (
                <>
                    {/* KPI Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <Card>
                            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                <CardTitle className="text-sm font-medium">Ventas Totales</CardTitle>
                                <DollarSign className="h-4 w-4 text-muted-foreground" />
                            </CardHeader>
                            <CardContent>
                                <div className="text-2xl font-bold">{formatCurrency(summary?.total_sales || 0)}</div>
                                <p className="text-xs text-muted-foreground">
                                    {summary?.total_transactions || 0} transacciones
                                </p>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                <CardTitle className="text-sm font-medium">Ganancia Bruta</CardTitle>
                                <TrendingUp className="h-4 w-4 text-emerald-500" />
                            </CardHeader>
                            <CardContent>
                                <div className="text-2xl font-bold text-emerald-600">{formatCurrency(summary?.gross_profit || 0)}</div>
                                <div className="flex items-center text-xs text-muted-foreground">
                                    <span className={summary?.margin_percentage >= 30 ? "text-emerald-500" : "text-amber-500"}>
                                        {summary?.margin_percentage}% Margen
                                    </span>
                                </div>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                <CardTitle className="text-sm font-medium">Costo Total</CardTitle>
                                <ArrowDownRight className="h-4 w-4 text-red-500" />
                            </CardHeader>
                            <CardContent>
                                <div className="text-2xl font-bold text-muted-foreground">{formatCurrency(summary?.total_cost || 0)}</div>
                                <p className="text-xs text-muted-foreground">Costo de mercadería vendida</p>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                <CardTitle className="text-sm font-medium">Ticket Promedio</CardTitle>
                                <CreditCard className="h-4 w-4 text-muted-foreground" />
                            </CardHeader>
                            <CardContent>
                                <div className="text-2xl font-bold">{formatCurrency(summary?.average_ticket || 0)}</div>
                                <p className="text-xs text-muted-foreground">Por transacción</p>
                            </CardContent>
                        </Card>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-7 gap-4">
                         {/* Sales Trend Chart */}
                         <Card className="lg:col-span-4">
                            <CardHeader>
                                <CardTitle>Tendencia de Ventas</CardTitle>
                            </CardHeader>
                            <CardContent className="pl-2">
                                <div className="h-[300px]">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <LineChart data={trends}>
                                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                            <XAxis 
                                                dataKey="date" 
                                                tickFormatter={(val) => format(new Date(val), 'dd MMM', { locale: es })}
                                                tickLine={false}
                                                axisLine={false}
                                                tickMargin={8}
                                                minTickGap={32}
                                            />
                                            <YAxis 
                                                tickFormatter={(val) => `$${val / 1000}k`}
                                                tickLine={false}
                                                axisLine={false}
                                                tickMargin={8}
                                            />
                                            <Tooltip 
                                                formatter={(val) => formatCurrency(val)}
                                                labelFormatter={(label) => format(new Date(label), 'dd MMMM yyyy', { locale: es })}
                                            />
                                            <Line 
                                                type="monotone" 
                                                dataKey="total_sales" 
                                                stroke="#4f46e5" 
                                                strokeWidth={2} 
                                                dot={false}
                                                activeDot={{ r: 4 }}
                                            />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Payment Methods Chart */}
                        <Card className="lg:col-span-3">
                            <CardHeader>
                                <CardTitle>Métodos de Pago</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="h-[300px]">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie
                                                data={paymentMethods}
                                                cx="50%"
                                                cy="50%"
                                                innerRadius={60}
                                                outerRadius={80}
                                                paddingAngle={5}
                                                dataKey="amount"
                                            >
                                                {paymentMethods.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                ))}
                                            </Pie>
                                            <Tooltip formatter={(val) => formatCurrency(val)} />
                                            <Legend verticalAlign="bottom" height={36} />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Top Products Table */}
                    <Card>
                        <CardHeader>
                            <CardTitle>Top Productos Vendidos</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-4">
                                {topProducts.map((product, idx) => (
                                    <div key={idx} className="flex items-center justify-between border-b pb-4 last:border-0 last:pb-0">
                                        <div className="space-y-1">
                                            <p className="font-medium">{product.product_name}</p>
                                            <p className="text-xs text-muted-foreground">{product.quantity_sold} unidades vendidas</p>
                                        </div>
                                        <div className="text-right">
                                            <p className="font-bold">{formatCurrency(product.total_sales)}</p>
                                            <p className="text-xs text-emerald-600">+{formatCurrency(product.profit)} ganancia</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                </>
            )}
        </div>
    );
}
